import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AuthState } from './store/slices/auth.slice';
import { RootState } from './store';
import { AuthApi } from './api/AuthApi';
import { isErrorCode } from './api/utils/ErrorHandler';
import { ErrorCodes } from './api/utils/errorCodes';
import { useContainer } from './hooks/useContainer';
import { ACCESS_TOKEN, LocalStorage } from './util/LocalStorage';
import { RoleTypeEnum } from './commonTypes/user.types';
import ErrorBoundary from './util/ErrorBoundary';
import LeaveRequest from './views/LeaveRequest';
import Members from './views/Members';
import PerformEvaluation from './views/Evaluation/PerformEvaluation';
import EvaluationPreview from './views/Evaluation/EvaluationPreview';
import Settings from './views/Settings';
import { OwnerLeaveRequest } from './views/LeaveRequest/Owner';
import { GlobalApi } from '../src/util/Global';
import useNotificationWebSocket from './hooks/useNotificationWebSocket';
import GlobalWebSocketHandler from './webSocketHandlers';
import { Loader } from './components/Loader';
import { useAddAuth, useJoinToken } from './hooks';
import { useSetResizeListener } from './hooks/windowEvents/useSetResizeListener';
import Projects from './views/Projects';
import Layout from './components/Layout';
import DocumentCategories from './views/DocumentCategories';
import { MixerLoader } from './components/Common';
import Evaluation from './views/Evaluation';
import CreateEvaluation from './views/Evaluation/CreateEvaluation';
import { InvoiceList } from './components/InvoiceTemplate';
import InvoiceTable from './components/Invoices/InvoiceTable';
import { SingleInvoicePreviewMobile } from './components/Invoices';
import { AppConfig } from './config/AppConfig';
import Project from './views/Project';
import { useHttpGetActiveSubscriptionOnMount, useHttpGetFeatureConfigsOnMount } from './hooks/organization/organizationApi';
import { useFeatureAvailability } from './hooks/features/useFeatureAvailability';
import { Feature } from '@cyberlabrs/helpers';
import { useFeatureLimitation } from './hooks/features';
import { useIntegrationsStatus } from './hooks';

const RoleChanged = lazy(() => import('./views/RoleChanged'));
const GoogleAuthCallback = lazy(() => import('./views/Auth/GoogleAuthCallback'));
const SlackAuthCallback = lazy(() => import('./views//Auth/SlackAuthCallback'));
const MicrosoftAuthCallback = lazy(() => import('./views//Auth/MicrosoftAuthCallback'));
const Auth = lazy(() => import('./views/Auth'));
const ConfirmSignUpView = lazy(() => import('./views/Auth/ConfirmSignUpView'));
const PageNotFoundView = lazy(() => import('./views/PageNotFound'));
const DashboardView = lazy(() => import('./views/Dashboard'));
const JoinOrganizationView = lazy(() => import('./views/JoinOrganization'));
const CalendarView = lazy(() => import('./views/Calendar'));
const GetStarted = lazy(() => import('./views/GetStarted'));
const Clients = lazy(() => import('./views/Clients'));
const ClientDetail = lazy(() => import('./views/ClientDetail'));
const TimeTracking = lazy(() => import('./views/TimeTracking'));
const AddEditPolicy = lazy(() => import('./views/AddEditPolicy'));
const AddEditBranch = lazy(() => import('./views/AddEditBranch'));
const AddEditDocumentCategory = lazy(() => import('./views/DocumentCategories/AddEditCategory'));
const BranchDetails = lazy(() => import('./views/BranchDetails'));
const Branches = lazy(() => import('./views/Branches'));
const AddEditProject = lazy(() => import('./views/AddEditProject'));
const Integrations = lazy(() => import('./views/Integrations'));
const Holidays = lazy(() => import('./views/Holidays'));
const AddEditHoliday = lazy(() => import('./views/AddEditHoliday'));
const authPaths = ['/sign-in', '/sign-up', '/sign-in-callback', '/slack-callback', '/microsoft-callback'];
const HolidaysImport = lazy(() => import('./views/HolidaysImport'));
const GoogleIntegrationCallback = lazy(() => import('./views/GoogleIntegrationCallback'));
const GoogleIntegrationPage = lazy(() => import('./views/GoogleIntegrationPage'));
const SlackIntegrationPage = lazy(() => import('./views/SlackIntegrationPage'));
const Policies = lazy(() => import('./views/Policies'));
const Profile = lazy(() => import('./views/Profile'));
const Teams = lazy(() => import('./views/Teams'));
const AddEditTeamForm = lazy(() => import('./views/AddEditTeam'));
const TeamOverview = lazy(() => import('./components/TeamOverview'));
const ClientsCreate = lazy(() => import('./views/AddClient'));
const NotificationSettings = lazy(() => import('./views/NotificationSettings'));
const EditMedicalLeave = lazy(() => import('./views/EditMedicalLeave'));
const AddEditInvoice = lazy(() => import('./components/AddEditInvoice'));
const BillingsPage = lazy(() => import('./views/Billing'));
const LifetimeSubscriptionPage = lazy(() => import('./views/LifetimeSubscription'));

const HandleIsCyberlab = (component: JSX.Element) => {
    const organizationId = useSelector<RootState, string>(state => state.auth.value.organizationId);
    const cyberlabOrganizationId = AppConfig.getCyberlabOrganizationId;
    const isCyberlab = organizationId === cyberlabOrganizationId;
    return isCyberlab ? component : <Navigate to={'*'} />;
};

const AuthenticatedRoutes = () => {
    return <Outlet />;
};

const AuthIsSignedIn = () => {
    const auth = useSelector<RootState, AuthState>(state => state.auth);
    const storage: LocalStorage = useContainer(LocalStorage);
    const { joinToken } = useParams();

    const access_token = storage.get(ACCESS_TOKEN);
    const isAuthed = Boolean(auth.value.userSub || access_token);

    useEffect(() => {
        if (!isAuthed) {
            storage.removeAuth();
        }
    }, [isAuthed]);

    if (!isAuthed) {
        return joinToken ? <Navigate to={`/sign-up?joinToken=${joinToken}`} /> : <Navigate to="/sign-in" />;
    }

    return <AuthenticatedRoutes />;
};

const AuthIsNotSignedIn = () => {
    const auth = useSelector<RootState, AuthState>(state => state.auth);
    const { joinToken } = useJoinToken();

    if (!auth.value.userSub) return <Outlet />;

    if (auth.value.organizationId) return <Navigate to={'/'} />;

    if (joinToken) return <Navigate to={`join-organization/${joinToken}`} />;

    return <Navigate to={'/get-started'} />;
};

const RoleGuard = (roles: RoleTypeEnum[], navigateTo: string) => {
    const auth = useSelector<RootState, AuthState>(state => state.auth);
    if (!auth.value.userSub) return;
    return <>{roles.includes(auth.value.role.name) ? <Outlet /> : <Navigate to={navigateTo} />}</>;
};

const FeatureGuard = (featureName: string, navigateTo: string = '/') => {
    const featureAvailability = useFeatureAvailability(featureName);
    if (!featureAvailability.isAvailable) {
        return <Navigate to={navigateTo} replace />;
    }
    return <Outlet />;
};

const SlackIntegrationGuard = ({ navigateTo = '/integrations' }: { navigateTo?: string }) => {
    const slackLimitation = useFeatureLimitation(Feature.INTEGRATIONS, 'slack');
    const { isReady } = useIntegrationsStatus();

    if (!isReady) {
        return <MixerLoader position="centerOnScreen" />;
    }

    if (slackLimitation.isDisabled) {
        return <Navigate to={navigateTo} replace />;
    }

    return <Outlet />;
};

const App = () => {
    const auth = useSelector<RootState, AuthState>(state => state.auth);
    const authApi: AuthApi = useContainer(AuthApi);
    const storage: LocalStorage = useContainer(LocalStorage);
    const globalApi: GlobalApi = useContainer(GlobalApi);
    const billingEnabled = true;
    const { addUserAuth } = useAddAuth();

    useSetResizeListener();
    useNotificationWebSocket(auth.value.userSub, storage.get(ACCESS_TOKEN));
    useHttpGetFeatureConfigsOnMount();
    useHttpGetActiveSubscriptionOnMount();

    useEffect(() => {
        const accessToken = storage.get(ACCESS_TOKEN);
        if (!auth.value.userSub && accessToken !== null) {
            const { exec } = authApi.auth({
                onSuccess: res => addUserAuth(res.body),
                onError: error => {
                    storage.removeAuth();
                    if (!authPaths.includes(globalApi.pathName())) {
                        if (isErrorCode(error, ErrorCodes.ROLE_MISMATCH)) return globalApi.href('/role-changed');
                        globalApi.href('/sign-in');
                    }
                },
            });
            exec();
        }
    }, [auth.value.userSub]);

    return (
        <BrowserRouter>
            <ErrorBoundary>
                <Suspense fallback={<MixerLoader position="centerOnScreen" />}>
                    <Loader type="authLoader" onLoading={<MixerLoader position="centerOnScreen" />}>
                        <Routes>
                            <Route element={<GlobalWebSocketHandler />}>
                                <Route element={<AuthIsNotSignedIn />}>
                                    <Route path="/sign-in" element={<Auth />} />
                                    <Route path="/sign-up" element={<Auth />} />
                                    <Route path="/auth/verification" element={<ConfirmSignUpView />} />
                                    <Route path="/sign-in-callback" element={<GoogleAuthCallback />} />
                                    <Route path="/slack-callback" element={<SlackAuthCallback />} />
                                    <Route path="/microsoft-callback" element={<MicrosoftAuthCallback />} />
                                    <Route path="/role-changed" element={<RoleChanged />} />
                                </Route>
                                <Route element={<AuthIsSignedIn />}>
                                    <Route
                                        element={RoleGuard(
                                            [RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN, RoleTypeEnum.ORDINARY, RoleTypeEnum.PM, RoleTypeEnum.TEAM_LEAD],
                                            '/'
                                        )}
                                    >
                                        <Route path="/google-integration-callback" element={<GoogleIntegrationCallback />} />
                                    </Route>
                                    <Route path="/get-started" element={<GetStarted />} />
                                    <Route path="/join-organization/:joinToken" element={<JoinOrganizationView />} />
                                    <Route element={<Layout />}>
                                        <Route path="/profile/:userSub" element={<Profile />} />
                                        <Route path="/notification-settings" element={<NotificationSettings />} />
                                        <Route
                                            element={RoleGuard(
                                                [
                                                    RoleTypeEnum.OWNER,
                                                    RoleTypeEnum.ADMIN,
                                                    RoleTypeEnum.ORDINARY,
                                                    RoleTypeEnum.PM,
                                                    RoleTypeEnum.TEAM_LEAD,
                                                ],
                                                '/get-started'
                                            )}
                                        >
                                            <Route path="/" element={<DashboardView />} />
                                            <Route path="/my-balance" element={<LeaveRequest />} />
                                            <Route path="/calendar" element={<CalendarView />} />
                                        </Route>
                                        <Route
                                            element={RoleGuard(
                                                [RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN, RoleTypeEnum.PM, RoleTypeEnum.TEAM_LEAD],
                                                '/'
                                            )}
                                        >
                                            <Route path="/leave-request/owner" element={<OwnerLeaveRequest />} />
                                        </Route>
                                        <Route element={RoleGuard([RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN], '/')}>
                                            <Route path="/clients/:clientId/edit" element={<ClientsCreate />} />
                                            <Route path="/clients/:clientId" element={<ClientDetail />} />
                                            <Route path="/clients" element={<Clients />} />
                                            <Route path="/clients-create" element={<ClientsCreate />} />
                                        </Route>
                                        <Route
                                            element={RoleGuard(
                                                [
                                                    RoleTypeEnum.OWNER,
                                                    RoleTypeEnum.ADMIN,
                                                    RoleTypeEnum.ORDINARY,
                                                    RoleTypeEnum.PM,
                                                    RoleTypeEnum.TEAM_LEAD,
                                                ],
                                                '/'
                                            )}
                                        >
                                            <Route path="/project/:id" element={<Project />} />
                                            <Route path="/projects" element={<Projects />} />
                                        </Route>
                                        <Route element={RoleGuard([RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN], '/')}>
                                            <Route path="/members" element={<Members />} />
                                        </Route>
                                        <Route element={RoleGuard([RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN], '/')}>
                                            <Route path="/organization" element={<Settings />} />
                                            <Route path="/policies" element={<Policies />} />
                                        </Route>
                                        <Route
                                            element={RoleGuard(
                                                [
                                                    RoleTypeEnum.OWNER,
                                                    RoleTypeEnum.ADMIN,
                                                    RoleTypeEnum.ORDINARY,
                                                    RoleTypeEnum.PM,
                                                    RoleTypeEnum.TEAM_LEAD,
                                                ],
                                                '/'
                                            )}
                                        >
                                            <Route path="/time-tracking" element={<TimeTracking />} />
                                            <Route path="/time-tracking/:tabName/:date" element={<TimeTracking />} />
                                        </Route>
                                        <Route
                                            element={RoleGuard(
                                                [
                                                    RoleTypeEnum.OWNER,
                                                    RoleTypeEnum.ADMIN,
                                                    RoleTypeEnum.ORDINARY,
                                                    RoleTypeEnum.PM,
                                                    RoleTypeEnum.TEAM_LEAD,
                                                ],
                                                '/'
                                            )}
                                        >
                                            <Route path="/integrations/google" element={<GoogleIntegrationPage />} />
                                            <Route element={<SlackIntegrationGuard navigateTo="/integrations" />}>
                                                <Route path="/integrations/slack" element={<SlackIntegrationPage />} />
                                            </Route>
                                        </Route>
                                        <Route element={RoleGuard([RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN], '/')}>
                                            <Route path="/create-policy" element={<AddEditPolicy />} />
                                            <Route path="/edit-policy/:policyId" element={<AddEditPolicy />} />
                                            <Route path="/edit-medical-leave/:medicalLeaveId" element={<EditMedicalLeave />} />
                                        </Route>
                                        <Route element={RoleGuard([RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN], '/')}>
                                            <Route path="/create-branch" element={<AddEditBranch />} />
                                            <Route path="/branches/:id" element={<BranchDetails />} />
                                            <Route path="/branches/:id/edit" element={<AddEditBranch />} />
                                            <Route path="/branches" element={<Branches />} />
                                        </Route>
                                        <Route
                                            element={RoleGuard(
                                                [
                                                    RoleTypeEnum.OWNER,
                                                    RoleTypeEnum.ADMIN,
                                                    RoleTypeEnum.ORDINARY,
                                                    RoleTypeEnum.PM,
                                                    RoleTypeEnum.TEAM_LEAD,
                                                ],
                                                '/'
                                            )}
                                        >
                                            <Route path="/documents" element={<DocumentCategories />} />
                                            <Route path="/create-category" element={<AddEditDocumentCategory />} />
                                            <Route path="/category/:id/edit" element={<AddEditDocumentCategory />} />
                                        </Route>
                                        <Route
                                            element={RoleGuard(
                                                [
                                                    RoleTypeEnum.OWNER,
                                                    RoleTypeEnum.ADMIN,
                                                    RoleTypeEnum.ORDINARY,
                                                    RoleTypeEnum.PM,
                                                    RoleTypeEnum.TEAM_LEAD,
                                                ],
                                                '/'
                                            )}
                                        >
                                            <Route path="/integrations" element={<Integrations />} />
                                        </Route>
                                        <Route
                                            element={RoleGuard(
                                                [
                                                    RoleTypeEnum.OWNER,
                                                    RoleTypeEnum.ADMIN,
                                                    RoleTypeEnum.PM,
                                                    RoleTypeEnum.TEAM_LEAD,
                                                    RoleTypeEnum.ORDINARY,
                                                ],
                                                '/'
                                            )}
                                        >
                                            <Route path="/teams" element={<Teams />} />
                                            <Route path="/create-team" element={<AddEditTeamForm />} />
                                            <Route path="/teams/:id/edit" element={<AddEditTeamForm />} />
                                            <Route path="/teams/:id" element={<TeamOverview />} />
                                        </Route>
                                        <Route
                                            element={RoleGuard(
                                                [
                                                    RoleTypeEnum.OWNER,
                                                    RoleTypeEnum.ADMIN,
                                                    RoleTypeEnum.ORDINARY,
                                                    RoleTypeEnum.PM,
                                                    RoleTypeEnum.TEAM_LEAD,
                                                ],
                                                '/'
                                            )}
                                        >
                                            <Route element={FeatureGuard(Feature.HOLIDAYS, '/')}>
                                                <Route path="/holidays" element={<Holidays />} />
                                                <Route path="/holidays/:year" element={<Holidays />} />
                                            </Route>
                                        </Route>
                                        <Route element={RoleGuard([RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN], '/')}>
                                            <Route path="/create-holiday" element={<AddEditHoliday />} />
                                            <Route path="/holidays/:id/edit" element={<AddEditHoliday />} />
                                            <Route path="/holidays-import" element={<HolidaysImport />} />
                                        </Route>
                                        <Route element={RoleGuard([RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN, RoleTypeEnum.PM], '/')}>
                                            <Route path="/create-project" element={<AddEditProject />} />
                                            <Route path="/projects/:id/edit" element={<AddEditProject />} />
                                        </Route>
                                        <Route
                                            element={RoleGuard(
                                                [
                                                    RoleTypeEnum.OWNER,
                                                    RoleTypeEnum.ADMIN,
                                                    RoleTypeEnum.ORDINARY,
                                                    RoleTypeEnum.PM,
                                                    RoleTypeEnum.TEAM_LEAD,
                                                ],
                                                '/'
                                            )}
                                        >
                                            <Route path="/perform-evaluation/:id" element={HandleIsCyberlab(<PerformEvaluation />)} />
                                            <Route path="/evaluation-preview/:id" element={HandleIsCyberlab(<EvaluationPreview />)} />
                                            <Route path="/evaluation" element={HandleIsCyberlab(<Evaluation />)} />
                                        </Route>
                                        <Route element={RoleGuard([RoleTypeEnum.ADMIN, RoleTypeEnum.OWNER], '/')}>
                                            <Route path="/create-evaluation" element={HandleIsCyberlab(<CreateEvaluation />)} />
                                        </Route>

                                        <Route element={RoleGuard([RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN], '/')}>
                                            <Route path="/invoices/create" element={<AddEditInvoice />} />
                                            <Route path="/invoices/:id/edit" element={<AddEditInvoice />} />
                                            <Route path="/invoices" element={<InvoiceTable />} />
                                            <Route path="/invoices/invoice-list" element={<InvoiceList />} />
                                            <Route path="/invoices/preview" element={<SingleInvoicePreviewMobile />} />
                                        </Route>
                                        {billingEnabled && (
                                            <Route element={RoleGuard([RoleTypeEnum.OWNER], '/')}>
                                                <Route path="/subscription" element={<BillingsPage />} />
                                                <Route path="/subscription/lifetime" element={<LifetimeSubscriptionPage />} />
                                            </Route>
                                        )}
                                    </Route>
                                    <Route path="*" element={<PageNotFoundView />} />
                                </Route>
                            </Route>
                        </Routes>
                    </Loader>
                </Suspense>
            </ErrorBoundary>
        </BrowserRouter>
    );
};

export default App;
